import { useMutation } from "@apollo/client/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Head } from "../../../../components/core";
import {
  NotifyType,
  useNotifyContext,
} from "../../../../contexts/NotifyContext";
import {
  ADD_FLEETVENDOR,
  FleetVendorForm,
  GET_FLEETVENDORS,
} from "../../../../graphql/fleets/settings/vendors";
import Form from "./components/Form";

const FleetVendorCreate = ({ breadcrumbs }: { breadcrumbs: Breadcrumb[] }) => {
  const { addNotify } = useNotifyContext();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [createFleetVendor] = useMutation(ADD_FLEETVENDOR, {
    refetchQueries: [{ query: GET_FLEETVENDORS }],
    awaitRefetchQueries: true,
  });

  const handleSubmit = (
    values: FleetVendorForm,
    actions: { setSubmitting: (arg0: boolean) => void }
  ) => {
    createFleetVendor({
      variables: {
        name: values.name,
        email: values.email,
        phoneNumber: values.phoneNumber,
        webAddress: values.webAddress,
        address: values.address,
        latitude: values.latitude,
        longitude: values.longitude,
        notes: values.notes,
        contactName: values.contactName,
        contactPhoneNumber: values.contactPhoneNumber,
        classification: values.classification,
        profileImageUrl: values.profileImageUrl,
        status: values.status,
      },
    })
      .then(({ data }) => {
        actions.setSubmitting(false);
        if (data?.fleetVendorCreate) {
          addNotify({
            type: NotifyType.SUCCESS,
            title: "Vendor created successfully",
          });
          return navigate(`/fleets/settings/vendors`);
        } else {
          addNotify({
            type: NotifyType.ERROR,
            title: "Vendor creation failed",
            message: "Something went wrong, please try again later",
          });
        }
      })
      .catch((error) => {
        actions.setSubmitting(false);
        addNotify({
          type: NotifyType.ERROR,
          title: "Vendor creation failed",
          message: error.message,
        });
      });
  };

  return (
    <>
      <Head
        title="Create Vendor"
        heading="Vendors"
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: "Fleet Settings",
            href: "/fleets/settings",
          },
          {
            name: "Vendors",
            href: "/fleets/settings/vendors",
          },
          {
            name: FleetVendorCreateResource.name,
            href: null,
          },
        ]}
      />
      <div className="mx-auto max-w-3xl py-6 sm:py-8">
        <div className="rounded-xl bg-greyish p-5">
          <div className="mb-6 sm:flex sm:items-center md:mb-8">
            <div className="sm:flex-auto">
              <h1 className="text-xl font-medium text-gray-900">
                {t("heading_create_fleet_vendor")}
              </h1>
              <p className="mt-2 text-sm text-gray-700">
                {t("description_create_fleet_vendor")}
              </p>
            </div>
          </div>

          <Form
            initialValues={{
              name: "",
              email: "",
              phoneNumber: "",
              webAddress: "",
              address: "",
              latitude: "",
              longitude: "",
              notes: "",
              contactName: "",
              contactPhoneNumber: "",
              classification: [],
              profileImageUrl: "",
              status: false,
            }}
            onSubmit={handleSubmit}
            actionLabel={t("text_create")}
          />
        </div>
      </div>
    </>
  );
};

export default FleetVendorCreate;
export const FleetVendorCreateResource: ResourceProps = {
  name: "Create Vendor",
  description: "Create a new fleet vendor",
  access: ["create-fleetVendors"],
  path: "vendors/add",
};
